import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/User';
import { TourMaskProvider } from '../components/tourMask/tourMaskHooks';
import configs from '../configs';
import UserService from '../modules/services/users/user.service';
import { getAllStorage, getFromSessionStorage } from '../utils/utils';
import { isKeycloakEnabled } from '../utils/keycloakAuth';
import PageLoading from '../components/PageLoading/PageLoading';
import LoginKeycloak from '../modules/LoginKeycloak/LoginKeycloak';

const LoginAuth = React.lazy(() => import('../modules/LoginAuth/LoginAuth'));
const Login = React.lazy(() => import('../modules/Login/Login'));
const LoginSSO = React.lazy(() => import('../modules/LoginSSO/LoginSSO'));
const Template = React.lazy(() => import('../modules/Template/Template'));
const Logout = React.lazy(() => import('../modules/Logout/Logout'));
const TourMask = React.lazy(() => import('../components/tourMask/tourMask'));

const Routes = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <RouteHandler />
    </BrowserRouter>
  );
};

const RouteHandler = () => {
  const location = useLocation();
  const { isLogged, user } = useUser();
  const [isTourOpen, setIsTourOpen] = useState(false);

  const userService = React.useMemo(() => new UserService(), []);
  const isNotPc = React.useMemo(
    () => window.navigator.userAgent?.match(/Mobile|Android|iPhone|iPad|iPod/i),
    [],
  );
  const [userLogout, setUserLogout] = useState(() =>
    sessionStorage.getItem('userLogout'),
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setUserLogout(sessionStorage.getItem('userLogout'));
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogoutChanged', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogoutChanged', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem('idtoken')) {
      localStorage.removeItem('idtoken');
    }
  }, []);

  useEffect(() => {
    const timeout1 = setTimeout(() => {
      if (
        !isLogged &&
        location.pathname !== '/' &&
        location.pathname.indexOf('/login_auth') === -1 &&
        location.pathname.indexOf('/competition') === -1 &&
        location.pathname.indexOf('/logout') === -1
      ) {
        window.location.href = '/';
      }
      const linkUrlRef =
        configs.env === 'prod'
          ? 'https://nps-new.ambevdevs.com.br'
          : 'https://nps-new-stg.ambevdevs.com.br';
      const existingScript = document.querySelector(
        `script[src="${linkUrlRef}"]`,
      );
      if (existingScript && existingScript.parentNode) {
        existingScript.remove();
      }
    }, 500);

    return () => {
      clearTimeout(timeout1);
    };
  }, [isLogged, location.pathname]);

  useEffect(() => {
    if (!isLogged || !user?.uuid_user) return;

    let originalFetch = null;
    let timeout2;

    timeout2 = setTimeout(() => {
      const sessionUser = getFromSessionStorage('user', user);

      if (!isLogged || !sessionUser?.uuid_user) {
        return;
      }

      const storage = isNotPc ? sessionStorage : localStorage;
      const npsClose =
        storage.getItem(`npsClose.${sessionUser?.objectId}`) ?? 0;
      const npsSkip = storage.getItem(`npsSkip.${sessionUser?.objectId}`) ?? 0;
      const nowTime = new Date().getTime();
      if (npsClose < nowTime && npsSkip < nowTime) {
        originalFetch = window.fetch;
        window.fetch = function (...args) {
          return originalFetch.apply(this, args).then(async response => {
            const { url } = response;
            const method = args[1]?.method?.toUpperCase() ?? 'GET';
            if (url.indexOf('ambevdevs.com.br/nps/surveys') > -1) {
              const data = {
                uuid_user: sessionUser?.uuid_user,
                str_name: sessionUser?.str_name,
                uuid_zone: sessionUser?.uuid_zone,
                uuid_country: sessionUser?.uuid_country,
                uuid_site: sessionUser?.uuid_site,
                str_email: sessionUser?.str_email,
              };

              if (method === 'GET') {
                if (response.status === 200 && data.uuid_user) {
                  userService.updateNpsLog({
                    ...data,
                    bol_need_popup: 200,
                    str_need_popup_url: url,
                  });
                  let sendPopup = false;
                  const listenInterval = setInterval(() => {
                    if (document.getElementById('ambevtech-nps')) {
                      const timer = setTimeout(() => {
                        userService.npsPopups({
                          ...data,
                          bol_popup_shown: '1',
                          external_status_type: 'POPUP_SHOWN',
                        });
                        sendPopup = true;
                        clearTimeout(timer);
                      }, 1000);
                      clearInterval(listenInterval);
                    }
                  }, 100);
                  const time = setTimeout(() => {
                    clearInterval(listenInterval);
                    if (!sendPopup) {
                      userService.npsPopups({
                        ...data,
                        bol_popup_shown: '0',
                        external_status_type: 'POPUP_SHOWN',
                      });
                    }
                    clearTimeout(time);
                  }, 5000);
                } else if (data.uuid_user) {
                  userService.updateNpsLog({
                    ...data,
                    initial_error_message: JSON.stringify(response),
                    bol_need_popup: response.status,
                    str_need_popup_url: url,
                  });
                }
              }
              if (method === 'POST' && data.uuid_user) {
                submitNpsPopup(data, response.status, url);
              }
            }

            return response;
          });
        };
      }
    });

    return () => {
      clearTimeout(timeout2);
      if (originalFetch) {
        window.fetch = originalFetch;
      }
    };
  }, [isLogged, user, isNotPc, userService]);

  const submitNpsPopup = (data, bol_submit, str_submit_url) => {
    userService.submitNpsPopup({
      ...data,
      external_status_type: 'SUBMITTED',
      bol_submit,
      str_submit_error_message: JSON.stringify(
        isNotPc ? getAllStorage('session') : getAllStorage('local'),
      ),
      str_submit_url,
    });
  };

  useEffect(() => {
    setIsTourOpen(true);
  }, []);

  const pathname = location.pathname;

  return (
    <TourMaskProvider>
      <Suspense fallback={<PageLoading />}>
        {pathname.indexOf('/login_auth') > -1 ||
        pathname.indexOf('/competition') > -1 ? (
          <LoginAuth />
        ) : pathname.indexOf('/logout') > -1 ? (
          <Logout />
        ) : userLogout || !(isLogged && user?.uuid_user) ? (
          configs.env === 'local' || configs.env === 'dev' ? (
            <Login />
          ) : isKeycloakEnabled() ? (
            <LoginKeycloak />
          ) : (
            <LoginSSO />
          )
        ) : (
          <Template />
        )}
      </Suspense>
      {isTourOpen && (
        <Suspense fallback={null}>
          <TourMask key={pathname + '/'} />
        </Suspense>
      )}
    </TourMaskProvider>
  );
};

export default Routes;
